/*
 * product-card.js — row-aware swatch-row collapse for card grids.
 *
 * The product card reserves a fixed-height meta-row (color swatches) so price
 * blocks line up across a grid row even when some cards have no swatches. On
 * mobile that reserved space looks empty when NO card in the row has swatches.
 *
 * CSS can't see a sibling card's contents, so this groups the grid's cards into
 * visual rows (by offsetTop) and, for any row where no card has swatches, adds
 * `pcard--swatch-row-empty` to those cards. component-product-card.css collapses
 * the meta-row for that class — but only inside the max-width: 749px query, so
 * desktop is unaffected even though the class is toggled at every breakpoint.
 *
 * Two grid shapes are covered: the listing grids (.product-grid), where each
 * cell wraps a .pcard, and the wishlist gallery ([data-wishlist-items]), where
 * the cell IS the card (.wl-item) and reuses the pcard__meta-row/pcard__swatches
 * classes (see snippets/wishlist-item.liquid).
 *
 * Re-runs on resize (column count changes which cards share a row) and when the
 * grid's contents change — replacement by filtering / pagination (Section
 * Rendering API) or a wishlist item being removed; both are childList mutations.
 */
(function () {
	if (window.__pcardSwatchRows) return;
	window.__pcardSwatchRows = true;

	var GRID_SELECTOR = '.product-grid, [data-wishlist-items]';
	/* Containers whose grid only appears after a lazy fetch (Dawn's
	   product-recommendations injects its .product-grid on intersection) — watched
	   from the start so the new grid is picked up and processed when it lands. */
	var LAZY_CONTAINER_SELECTOR = 'product-recommendations';
	var EMPTY_CLASS = 'pcard--swatch-row-empty';
	var MOBILE = '(max-width: 749px)';
	var observed = new WeakSet();
	var raf = null;

	function isMobile() {
		return window.matchMedia(MOBILE).matches;
	}

	/* The element that carries EMPTY_CLASS: the .pcard inside a listing-grid cell,
	   or the wishlist cell itself (the .wl-item is the card). */
	function cardOf(cell) {
		if (!cell.querySelector) return null;
		if (cell.classList.contains('wl-item')) return cell;
		return cell.querySelector('.pcard');
	}

	function clear(grid) {
		grid.querySelectorAll('.' + EMPTY_CLASS).forEach(function (card) {
			card.classList.remove(EMPTY_CLASS);
		});
	}

	function groupAndApply(grid) {
		var items = Array.prototype.filter.call(grid.children, function (el) {
			return !!cardOf(el);
		});
		if (!items.length) return;

		// Group consecutive grid cells into rows: cells in the same row share an
		// offsetTop (grid items are top-aligned). Stable across collapses, since
		// collapsing only shrinks a whole row uniformly.
		var rows = [];
		var rowTop = null;
		var row = null;
		items.forEach(function (item) {
			var top = item.offsetTop;
			if (rowTop === null || Math.abs(top - rowTop) > 1) {
				row = [];
				rows.push(row);
				rowTop = top;
			}
			row.push(item);
		});

		rows.forEach(function (rowItems) {
			var anySwatch = rowItems.some(function (item) {
				return !!item.querySelector('.pcard__swatches');
			});
			rowItems.forEach(function (item) {
				var card = cardOf(item);
				if (card) card.classList.toggle(EMPTY_CLASS, !anySwatch);
			});
		});
	}

	function observe() {
		document.querySelectorAll(GRID_SELECTOR).forEach(function (grid) {
			var parent = grid.parentElement;
			if (!parent || observed.has(parent)) return;
			observed.add(parent);
			// childList only — toggling our class is an attribute change and
			// won't retrigger the observer, so there is no feedback loop.
			new MutationObserver(schedule).observe(parent, {
				childList: true,
				subtree: true
			});
		});
		document.querySelectorAll(LAZY_CONTAINER_SELECTOR).forEach(function (container) {
			if (observed.has(container)) return;
			observed.add(container);
			new MutationObserver(schedule).observe(container, {
				childList: true,
				subtree: true
			});
		});
	}

	function run() {
		// Mobile-only behavior: above the breakpoint, clear any leftover class so
		// desktop is never touched (the CSS rule is also gated to the same query).
		var mobile = isMobile();
		document.querySelectorAll(GRID_SELECTOR).forEach(function (grid) {
			if (mobile) {
				groupAndApply(grid);
			} else {
				clear(grid);
			}
		});
		observe();
	}

	function schedule() {
		if (raf) cancelAnimationFrame(raf);
		raf = requestAnimationFrame(run);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', run);
	} else {
		run();
	}

	window.addEventListener('resize', schedule);
	window.addEventListener('load', schedule);
	document.addEventListener('shopify:section:load', schedule);
})();
